﻿/// <reference path="../../typings/app.d.ts" />

import { Feature, FeatureType } from "mapping";
import { ErrorLevel, LocationData, LocationType, PliMapViewModel, UnmappableLocation } from "mapping/forms-map";

import * as ko from "pli/knockout";
import { ServerCalculation, ServerCalculationContext, ServerCalculationContributor } from "../common-server-computed-ts";


/**
 * A legal description for a location.
 */
export interface LegalDescription {
    format: string;
    location: string;
    sections: string;
}


interface LocationError {
    id: string;
    errorLevel: string;
    message: string;
}


/**
 * The map data to send with the api call.
 */
export interface Location {
    /**
     * The unique id of the location.
     */
    id: string;
    /**
     * true if this location is valid, false otherwise.
     */
    isValid: boolean;
    /**
     * The legal description of this location.
     */
    legalDescription: LegalDescription;
    /**
     * The location's short display name.
     */
    shortDisplayName: string;
    /**
     * The location's long display name.
     */
    longDisplayName: string;
    /**
     * A callback function to navigate to the location.
     */
    openItem: () => void;
}


class LocationDictionary {
    [id: string]: Location;
}


/**
 * Options to supply when creating a MapDataViewModel.
 */
export interface MapServerComputedOptions {
    /**
     * The data to send with the map api call.
     */
    mapRequestData: ko.Observable<Location[]>;
    /**
     * Called when a feature is added to the map (can be used to create popups).
     */
    mapFeatureCallback?: (f: Feature) => void;
    /**
     * Called when the user clicks the reset button or when the shape data is updated. Defaults
     * to focusing on FeatureType.Land if undefined or doing nothing if null.
     */
    mapFocusCallback?: () => void;
    /**
     * The api to call to get map shape data.
     * @default "Crossings-Map-LoadMapData"
     */
    callName?: string;
    /**
     * When "false", prevents the api call from executing.
     */
    isReady?: ko.Observable<boolean>;
    /**
     * The message to display when the map does not contain any locations.
     * @default "No locations to show."
     */
    emptyMessage?: string;
    /**
     * A display name for the plural version of the item types being displayed on the map.
     * @default "Locations"
     */
    pluralEntityType?: string;
}

interface ServerResponseData {
    message?: string;
    data?: string;
    unmappableLocations?: LocationError[];
}


/**
 * A view model for loading and displaying a map.
 */
export class MapServerComputed {
    /**
     * Creates a new instance of the MapDataViewModel class.
     */
    constructor(options: MapServerComputedOptions) {
        if (!options) { throw new Error("options must be defined."); }
        if (!ko.isObservable(options.mapRequestData)) { throw new Error("options.mapRequestData must an observable."); }
        if (options.mapFeatureCallback && typeof options.mapFeatureCallback !== "function") { throw new Error("options.mapFeatureCallback must be a function."); }
        if (options.mapFocusCallback && typeof options.mapFocusCallback !== "function") { throw new Error("options.mapFocusCallback must be a function."); }
        if (options.callName && typeof options.callName !== "string") { throw new Error("options.callName must be a string."); }
        if (options.emptyMessage && typeof options.emptyMessage !== "string") { throw new Error("options.emptyMessage must be a string."); }
        if (options.pluralEntityType && typeof options.pluralEntityType !== "string") { throw new Error("options.pluralEntityType must be a string."); }

        // If options.mapFocusCallback is explicitly undefined, use default focus
        this._mapFocusCallback = options.mapFocusCallback !== undefined ? options.mapFocusCallback : () => {
            if (!this.mapData.map.adapter()) {
                // If no map bound, nothing to focus
                return;
            }
            if (!this.mapData.map.hasFeatures(FeatureType.Land)) {
                return;
            }
            this.mapData.map.center(FeatureType.Land, true); // true: zoomToBounds
        };

        this._hasGenericError = ko.observable(false);
        this._hideControls = ko.observable(false);
        this._refreshObservable = ko.observable();
        this.mapData = new PliMapViewModel({
            mapFeatureCallback: options.mapFeatureCallback,
            mapFocusCallback: this._mapFocusCallback,
            emptyMessage: options.emptyMessage,
            pluralEntityType: options.pluralEntityType,
            refreshData: (): void => this.refresh(),
            focusMapOnLoad: true,
            getLocationData: (featureProperties: any): LocationData[] => this._getLocationData(featureProperties),
            hideControls: this._hideControls,
            showFullscreenButton: true,
            onFullscreenToggle: (isFullscreen: boolean): void => this.focusMap()
        });

        this._rawMapRequestData = options.mapRequestData;
        this.extras = () => { return; };
        this.extras.state = {
            isReady: options.isReady
                ? options.isReady
                : ko.observable(true)
        };

        // Calculate mapRequestData outside of buildServerCalculationData so that
        // we recalculate state without waiting for the 2000ms timeout
        this._mapRequestData = ko.computed(() => {
            // Hack - subscribe to refresh observable so we can force refresh
            this._refreshObservable();
            this.mapData.loadViewModel({
                isLoading: true,
                unmappableLocations: [],
                hasGenericError: false,
                hasMultipleLocations: false
            });

            const rawMapRequestData = this._rawMapRequestData() || [];

            return rawMapRequestData.filter((location: Location) => {
                // Don't send locations to the server we know to be invalid
                return location.isValid;
            });
        });

        const contributor: ServerCalculationContributor = {
            applyServerCalculationResult: (context: ServerCalculationContext): void => this._applyServerCalculationResult(context),
            buildServerCalculationData: (): any => this._buildServerCalculationData(),
            state: this.extras.state
        };
        this.extras.serverCalculation = new ServerCalculation(options.callName || "Geo-Map-LoadMapData", contributor, 2000);

        this.focusMap = () => {
            if (this._mapFocusCallback) {
                this._mapFocusCallback();
            }
        };
    }


    /**
     * Refreshes the map with new data from the server.
     */
    refresh(): void {
        this._refreshObservable.valueHasMutated();
    }


    hideControls(): void {
        this._hideControls(true);
    }


    showControls(): void {
        this._hideControls(false);
    }


    forceLoadingState(): void {
        this.mapData.loadViewModel({
            isLoading: true,
            hasGenericError: false,
            hasMultipleLocations: false,
            unmappableLocations: []
        });
    }


    /**
     * Applies the response from the api call.
     * @param {Object} serverCalculationContext The context to apply from the serverComputed.
     */
    private _applyServerCalculationResult(serverCalculationContext: ServerCalculationContext): void {
        const response = serverCalculationContext.rootData;

        if (response.isError) { // Call failed
            this.mapData.loadViewModel({
                isLoading: false,
                hasGenericError: true,
                hasMultipleLocations: false,
                unmappableLocations: []
            });
        } else {
            const result = response.result;
            const locationErrors = this._mergeLocationErrors(result.unmappableLocations);
            const rawData = this._rawMapRequestData.peek();
            this.mapData.loadViewModel({
                isLoading: false,
                hasGenericError: false,
                hasMultipleLocations: rawData && rawData.length > 1,
                unmappableLocations: this._locationErrorsToUnmappableLocations(locationErrors),
                landData: result.data
            });
        }
    }


    /**
     * Creates the data required for the api call.
     */
    private _buildServerCalculationData(): any {
        const data = this._mapRequestData();
        const rawData = this._rawMapRequestData.peek();

        this._locationDictionary = {};
        rawData.forEach((location: any) => {
            this._locationDictionary[location.id] = location;
        });

        if (!data || !data.length) {
            const locationErrors = this._mergeLocationErrors([]);
            this.mapData.loadViewModel({
                isLoading: false,
                hasGenericError: false,
                hasMultipleLocations: rawData && rawData.length > 1,
                unmappableLocations: this._locationErrorsToUnmappableLocations(locationErrors)
            });
            return null; // No call will be made
        }

        return data;
    }


    /**
     * Merges unmappable locations returned from the api call with invalid locations from the raw map data.
     * @param {Array} unmappableLocations The unmappable locations returned from the api call.
     */
    private _mergeLocationErrors(unmappableLocations: LocationError[]): LocationError[] {
        unmappableLocations = unmappableLocations || [];
        const rawMapRequestData = this._rawMapRequestData();

        if (rawMapRequestData && rawMapRequestData.length) {
            unmappableLocations = unmappableLocations.concat(rawMapRequestData.filter((location: Location) => {
                return !location.isValid;
            }).map((location: Location) => {
                return {
                    id: location.id,
                    errorLevel: "error",
                    message: (location.legalDescription.format === "nts" ? "The NTS" : "The DLS") + " location is not entered correctly"
                };
            }));
        }

        return unmappableLocations;
    }


    private _locationErrorsToUnmappableLocations(locationErrors: LocationError[]): UnmappableLocation[] {
        return locationErrors.map((error: LocationError) => {
            const location = this._locationDictionary[error.id];
            return {
                id: location.id,
                errorLevel: error.errorLevel === "warning" ? ErrorLevel.Warning : ErrorLevel.Error,
                message: error.message,
                titleDisplayName: location.shortDisplayName,
                tooltipDisplayName: location.longDisplayName,
                openItem: location.openItem
            };
        });
    }


    private _getLocationData(properties: any): LocationData[] {
        const ids = properties.docId.split("|");
        return ids.map((id: string) => {
            const location = this._locationDictionary[id];
            return {
                id: location.id,
                type: location.legalDescription.format === "nts" ? LocationType.Nts : LocationType.Dls,
                titleDisplayName: location.shortDisplayName,
                tooltipDisplayName: location.longDisplayName,
                openItem: location.openItem
            };
        });
    }


    /**
     * The map being shown.
     */
    mapData: PliMapViewModel;
    /**
     * Extras for the MapDataViewModel.
     */
    extras: any;
    /**
     * Calls the mapFocusCallback (if it was provided to the constructor).
     */
    focusMap: () => void;


    private _hasGenericError: ko.Observable<boolean>;
    private _hideControls: ko.Observable<boolean>;
    private _locationDictionary: LocationDictionary;
    private _mapFocusCallback: () => void;
    private _mapRequestData: ko.Computed<Location[]>;
    private _rawMapRequestData: ko.Observable<Location[]>;
    private _refreshObservable: ko.Observable<any>;
}



// WEBPACK FOOTER //
// ./app/widget-map-ts/mapServerComputed.ts